import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import { CaretDownIcon } from '../../images/shapes';

const resultsPerPageDropdownDefaultPropResults = [10, 20, 50, 100, 200];

export type ResultsPerPageChangeFn = (results: number) => void;

export interface ResultsPerPageDropdownProps {
  /**
   * Options open above
   */
  showAbove?: boolean;
  /**
   * Additional classes for results dropdown
   */
  additionalClasses?: string;
  /**
   * Results options
   */
  results?: number[];
  /**
   * Chosen results count
   */
  setResultsPerPage: ResultsPerPageChangeFn;
  /**
   * Default selected value
   */
  value?: number;
  /**
   * Default selected value
   */
  label?: string;
  /**
   * Additional classes for button
   */
  additionalButtonClasses?: string;
  /**
   * If dropdown is disabled
   */
  disabled?: boolean;
  /**
   * Component test id
   */
  testId?: string;
}

/**
 * Component for pagination navigation
 */
const ResultsPerPageDropdown = ({
  additionalClasses = '',
  results = resultsPerPageDropdownDefaultPropResults,
  setResultsPerPage,
  value = 10,
  showAbove = false,
  label = '',
  additionalButtonClasses = '',
  disabled = false,
  testId = '',
}: ResultsPerPageDropdownProps) => {
  const [currentValue, setCurrentValue] = useState(value);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  return (
    <div
      className={twMerge(
        'py-3 flex flex-col items-center justify-center space-y-5',
        disabled &&
          'cursor-not-allowed opacity-50 pointer-events-none text-sm xl:text-base',
        additionalClasses,
      )}
      {...getTestProps(testId, 'wrapper')}
    >
      <div className="font-medium flex flex-row items-center whitespace-nowrap dark:text-white">
        {label}
        <Listbox
          value={currentValue}
          onChange={(val) => {
            setCurrentValue(val);
            setResultsPerPage(val);
          }}
        >
          {({ open }) => (
            <div className="ml-3 relative">
              <ListboxButton
                className={twMerge(
                  'relative w-full border rounded-md pl-4 pr-6 2xl:pr-10',
                  'py-1 2xl:py-2 cursor-pointer focus:outline-hidden bg-white dark:bg-slate-950',
                  'border-slate-400/80 dark:border-slate-800 data-open:border-blue',
                  additionalButtonClasses,
                )}
                {...getTestProps(testId, 'button')}
              >
                <span className="block leading-none text-blue-600 dark:text-white text-sm 2xl:text-base font-semibold">
                  {currentValue}
                </span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <CaretDownIcon
                    className={twMerge(
                      'transition-transform w-2 2xl:w-3 text-slate-900 dark:text-white',
                      open ? 'rotate-180 ' : '',
                    )}
                    aria-hidden="true"
                  />
                </span>
              </ListboxButton>
              <ListboxOptions
                as="ul"
                className={twMerge(
                  'absolute mt-1 z-10 w-full bg-white dark:bg-slate-900 rounded-lg shadow-xl',
                  'max-h-60 py-1 text-base text-base overflow-auto',
                  'focus:outline-hidden sm:text-sm dark:border dark:border-slate-800',
                  'transition-opacity ease-in duration-100 data-closed:opacity-0',
                  showAbove ? 'mb-2 bottom-full' : 'mt-2 top-full',
                )}
                transition
                modal={false}
              >
                {results.map((result) => (
                  <ListboxOption
                    as="li"
                    key={result}
                    className={twMerge(
                      'text-black dark:text-white',
                      'cursor-pointer text-base relative py-1.5 pl-4 pr-8 hover:text-blue-600 hover:font-bold',
                      'data-active:text-blue-600 data-active:font-bold',
                      'data-selected:text-blue-600 data-selected:font-bold',
                    )}
                    value={result}
                  >
                    {result}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </div>
          )}
        </Listbox>
      </div>
    </div>
  );
};

export default ResultsPerPageDropdown;
